/**
 * ENHANCED MODELS for the **ground** — the seam that was missing.
 *
 * Until this pass the switch reached exactly one loader,
 * `flight/aircraft-mesh.ts`, which draws the aeroplanes and the three raid
 * targets. Everything the *world* places went through `world-shared-ref.ts` and
 * never consulted the manifest at all, so the same `TERR1-52` drew as an
 * enhanced `.glb` when a bombing mission spawned it and as the bake's own flat
 * plates when Verdun placed one in a town — the same id, two different meshes,
 * in the same sortie. This is the second half of that lookup.
 *
 * **What it does not do is re-decide anything.** The geometry comes back in the
 * `.glb`'s own frame, which is already the world's (`enhanced-model.ts`: the
 * generator builds in the baked model's axes and exports through glTF's default
 * Y-up conversion, which is exactly the app's `MODEL_TO_WORLD_PITCH_RAD`), so
 * unlike the baked path this applies **no rotation** — one that needed it would
 * be a generator bug. Colour rides in `COLOR_0` exactly as it does on a baked
 * mesh, which is what lets the instancer's one shared `MeshBasicMaterial` draw
 * both without knowing which it has.
 *
 * **A building is one rung.** `enhanced-lod.ts` returns a single-rung model
 * untouched and names scenery as that case; `scenery.py` writes no `LOD<n>`
 * nodes, so there is nothing to ladder and nothing to switch. A building does
 * not bank either, so `COLOR_0` frozen at the bake's reference attitude — which
 * is wrong for an aeroplane and is why the fleet carries `_SHADEANCHOR` — is
 * exactly right here. The terrain is not shaded by the engine at all
 * (`world/terrain-palette.ts`: `SHADES.DAT` reports `halfWidth = 0` for every
 * terrain colour), so there is no ramp for a ground object to walk.
 */

import * as THREE from "three/webgpu";
import { loadEnhancedModel } from "./enhanced-model.ts";
import { colourToRgb, dimTerrainColour } from "../world/terrain-palette.ts";

/**
 * Every `Mesh` under a loaded enhanced object, as one geometry.
 *
 * `scenery.py` writes a single mesh, so this is a merge of one in practice —
 * but a generator that later splits a building into named parts should not
 * silently lose all but the first, which is the failure `world-instancer` could
 * not see (it would just draw less building).
 */
function geometriesUnder(object: THREE.Object3D): THREE.BufferGeometry[] {
  const found: THREE.BufferGeometry[] = [];
  object.traverse((node) => {
    const mesh = node as THREE.Mesh;
    if (mesh.isMesh && mesh.geometry) found.push(mesh.geometry);
  });
  return found;
}

/**
 * Walks every vertex colour down its own run for the hour — the same move
 * `world-shared-ref.ts` makes on a baked placement, applied to the enhanced
 * geometry so a dusk town does not have half its buildings at noon.
 */
function dim(geometry: THREE.BufferGeometry, shift: number): void {
  const attribute = geometry.getAttribute("color");
  if (!attribute || shift === 0) return;
  const array = attribute.array as Float32Array;
  for (let i = 0; i < array.length; i += 3) {
    const packed =
      (Math.round(array[i]! * 255) << 16) | (Math.round(array[i + 1]! * 255) << 8) | Math.round(array[i + 2]! * 255);
    const [r, g, b] = colourToRgb(dimTerrainColour(packed, shift));
    array[i] = r;
    array[i + 1] = g;
    array[i + 2] = b;
  }
  attribute.needsUpdate = true;
}

/**
 * The enhanced geometry for one placed object, or `undefined` if it has none or
 * could not be read.
 *
 * Failure is not fatal and never should be: a mixed world — some buildings
 * enhanced, some baked — is the designed case, exactly as a mixed fleet is
 * (`flight/aircraft-mesh.ts`). A region whose `/enhanced` route is not wired up
 * simply draws the bake, which is what it drew before this file existed.
 */
export async function loadEnhancedStructure(path: string, rampShift: number): Promise<THREE.BufferGeometry | undefined> {
  let object: THREE.Object3D;
  try {
    object = await loadEnhancedModel(path);
  } catch {
    return undefined;
  }
  const geometries = geometriesUnder(object);
  const first = geometries[0];
  if (!first) return undefined;
  // One mesh is the shape `scenery.py` writes; more than one is merged by
  // concatenating position and colour, which is all the instancer's material
  // reads.
  const geometry = geometries.length === 1 ? first.clone() : mergeGeometries(geometries);
  for (const source of geometries) source.dispose();
  dim(geometry, rampShift);
  geometry.computeBoundingSphere();
  return geometry;
}

export interface StructureSwap {
  /**
   * Distinct refs the switch has anything to say about, and how many placements
   * they cover.
   *
   * Reported rather than assumed, for the reason `enhanced-models.md` §5.1.1
   * exists: a `/enhanced` route that answers 200 with the SPA's own HTML leaves
   * both of these zero, the switch does nothing, and nothing on screen says so.
   *
   * **Live, and they start at zero.** The `.glb`s are no longer awaited before
   * the region is built (`world-shared-ref.ts`), so these count what has landed
   * so far and climb as the fetches finish. A caller that wants the settled
   * numbers — a census, a test — awaits `ready` first.
   */
  readonly refs: number;
  readonly placements: number;
  readonly set: (enabled: boolean) => void;
  /**
   * Resolves when every enhanced fetch this region started has settled, one way
   * or the other. **Nothing on the drawing path waits for it**: it exists so a
   * census can state a number instead of racing one.
   */
  readonly ready: Promise<void>;
}

/**
 * The ENHANCED MODELS switch for a region's buildings, resolved once out of the
 * instancer's own per-ref state.
 *
 * A geometry assignment per distinct building, not a rebuild: the placements,
 * their matrices, their instance count and the terrain lines are all unchanged,
 * so flipping the setting over a town costs one assignment each. That is the
 * arrangement `setLandcoverShading` already has for the plates' two colour
 * buffers, and it is why both geometries are loaded whatever the setting says.
 *
 * A ref with no enhanced geometry — every road, river, field plate, hill and
 * tree, and any building the roster declines — is simply not in the list, and
 * the toggle does nothing to it. A mixed world is the designed case, exactly as
 * a mixed fleet is.
 */
export function structureSwap(
  entries: readonly (readonly [string, readonly unknown[]])[],
  loaded: readonly (
    | { readonly geometry: THREE.BufferGeometry; readonly enhancedGeometry?: Promise<THREE.BufferGeometry | undefined> }
    | undefined
  )[],
  meshes: ReadonlyMap<string, THREE.InstancedMesh>,
): StructureSwap {
  interface Structure {
    readonly mesh: THREE.InstancedMesh;
    readonly baked: THREE.BufferGeometry;
    readonly enhanced: THREE.BufferGeometry;
    readonly placements: number;
  }
  const structures: Structure[] = [];
  /**
   * What the switch is currently set to, remembered because a `.glb` can land
   * *after* the player has already turned the setting on — the whole reason
   * this is no longer a one-shot list. Without it a building that arrived late
   * would sit at its bake until the next flip.
   */
  let enabledNow = false;
  const draw = (entry: Structure, enabled: boolean): void => {
    const wanted = enabled ? entry.enhanced : entry.baked;
    if (entry.mesh.geometry === wanted) return;
    entry.mesh.geometry = wanted;
    // The two differ in extent — an enhanced building has a pitched roof
    // where the bake had a flat card — so the culling sphere has to follow,
    // or a town pops out of view at the wrong distance in one state.
    entry.mesh.computeBoundingSphere();
  };
  const pending = entries.map(async ([objectRef, refPlacements], slot) => {
    const shared = loaded[slot];
    const mesh = meshes.get(objectRef);
    if (!shared?.enhancedGeometry || !mesh) return;
    const enhanced = await shared.enhancedGeometry;
    if (!enhanced) return;
    const entry: Structure = { mesh, baked: shared.geometry, enhanced, placements: refPlacements.length };
    structures.push(entry);
    draw(entry, enabledNow);
  });
  return {
    get refs(): number {
      return structures.length;
    },
    get placements(): number {
      return structures.reduce((total, entry) => total + entry.placements, 0);
    },
    ready: Promise.all(pending).then(() => undefined),
    set: (enabled: boolean) => {
      enabledNow = enabled;
      for (const entry of structures) draw(entry, enabled);
    },
  };
}

/** Position and colour from several geometries, concatenated into one non-indexed buffer. */
function mergeGeometries(geometries: readonly THREE.BufferGeometry[]): THREE.BufferGeometry {
  const parts = geometries.map((geometry) => (geometry.index ? geometry.toNonIndexed() : geometry));
  const total = parts.reduce((sum, part) => sum + (part.getAttribute("position")?.count ?? 0), 0);
  const positions = new Float32Array(total * 3);
  const colors = new Float32Array(total * 3);
  let at = 0;
  for (const part of parts) {
    const position = part.getAttribute("position");
    const colour = part.getAttribute("color");
    if (!position) continue;
    for (let index = 0; index < position.count; index += 1) {
      positions[(at + index) * 3] = position.getX(index);
      positions[(at + index) * 3 + 1] = position.getY(index);
      positions[(at + index) * 3 + 2] = position.getZ(index);
      colors[(at + index) * 3] = colour ? colour.getX(index) : 1;
      colors[(at + index) * 3 + 1] = colour ? colour.getY(index) : 1;
      colors[(at + index) * 3 + 2] = colour ? colour.getZ(index) : 1;
    }
    at += position.count;
  }
  const merged = new THREE.BufferGeometry();
  merged.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  merged.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  return merged;
}
